import { forwardRef, useImperativeHandle } from 'react';
import { PlayIcon, PauseIcon, BackwardIcon, ForwardIcon } from '@heroicons/react/24/solid';
import { Button } from '@/components/ui';
import { useVideoPlayer } from '@/hooks';
import { useStableMediaSrc } from '@/hooks/useStableMediaSrc';
import { formatDuration } from '@/utils/formatters';
import { cn } from '@/utils/cn';

interface VideoPlayerProps {
  src: string;
  className?: string;
  onTimeUpdate?: (currentTime: number, currentFrame: number) => void;
  onFrameChange?: (frame: number) => void;
  fps?: number;
  showControls?: boolean;
  autoPlay?: boolean;
}

export interface VideoPlayerRef {
  seekToFrame: (frame: number) => void;
  play: () => void;
  pause: () => void;
  currentFrame: number;
}

const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(function VideoPlayer({
  src,
  className,
  onTimeUpdate,
  onFrameChange,
  fps = 30,
  showControls = true,
  autoPlay = false,
}, ref) {
  const {
    videoRef,
    isPlaying,
    currentTime,
    duration,
    currentFrame,
    totalFrames,
    isReady,
    togglePlay,
    seek,
    seekToFrame,
    nextFrame,
    previousFrame,
    play,
    pause,
  } = useVideoPlayer({ onTimeUpdate, onFrameChange, fps });

  // Keep playback position stable across a signed-URL refresh (the `src`
  // prop is re-minted roughly every ~9 minutes by useMediaUrl upstream) —
  // see backlog/media-url-refresh-playback-position.md.
  useStableMediaSrc(videoRef, src);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    seekToFrame,
    play,
    pause,
    currentFrame,
  }), [seekToFrame, play, pause, currentFrame]);

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    seek(parseFloat(e.target.value));
  };

  return (
    <div className={cn('flex flex-col bg-black rounded-lg overflow-hidden', className)}>
      {/* Video element */}
      <div className="relative aspect-video bg-black">
        <video
          ref={videoRef}
          src={src}
          className="w-full h-full"
          autoPlay={autoPlay}
          playsInline
        >
          Your browser does not support the video tag.
        </video>
      </div>

      {/* Controls */}
      {showControls && (
        <div className="bg-dark-base p-4 space-y-3">
          {/* Timeline */}
          <div className="space-y-1">
            <input
              type="range"
              min={0}
              max={duration}
              value={currentTime}
              onChange={handleSeek}
              disabled={!isReady}
              className="w-full h-2 bg-dark-elevated-2 rounded-lg appearance-none cursor-pointer accent-accent"
              aria-label="Video timeline"
            />
            <div className="flex justify-between text-xs text-text-muted">
              <span>{formatDuration(currentTime)}</span>
              <span>
                Frame {currentFrame} / {totalFrames}
              </span>
              <span>{formatDuration(duration)}</span>
            </div>
          </div>

          {/* Playback controls */}
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={previousFrame}
              disabled={!isReady || currentFrame === 0}
              className="text-white hover:bg-dark-elevated-1"
              aria-label="Previous frame"
            >
              <BackwardIcon className="h-5 w-5" />
            </Button>

            <Button
              variant="primary"
              size="md"
              onClick={togglePlay}
              disabled={!isReady}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <PauseIcon className="h-6 w-6" />
              ) : (
                <PlayIcon className="h-6 w-6" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={nextFrame}
              disabled={!isReady || currentFrame >= totalFrames}
              className="text-white hover:bg-dark-elevated-1"
              aria-label="Next frame"
            >
              <ForwardIcon className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

export default VideoPlayer;
